/**
 * PATH: packages/ui/src/components/settings/TeamTab.tsx
 * PURPOSE: Full team management inside settings modal (orchestrator)
 *
 * WHY: Admins need to manage team members, invite new users, update roles,
 *      and remove members without leaving the settings modal.
 *
 * FLOW:
 *   ┌──────────────┐    ┌────────────────┐    ┌──────────────┐
 *   │ List Members │───▶│ Invite/Edit    │───▶│ Save Changes │
 *   └──────────────┘    └────────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - Backend API: /api/organisations/<id>/members/
 *   - ./team-tab/team-tab-primitives: shared types, Toast, ConfirmDialog
 *   - ./team-tab/MemberRow: single member row
 *   - ./team-tab/InviteModal: invite form modal
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Users, UserPlus, RefreshCw, Search } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { SettingsApiClient } from './types'

// Re-export shared types so existing consumers keep working
export type { TeamMember } from './team-tab/team-tab-primitives'
import {
  type TeamMember,
  type RoleOption,
  FALLBACK_ROLE_OPTIONS,
  Toast,
  ConfirmDialog,
} from './team-tab/team-tab-primitives'
import { MemberRow } from './team-tab/MemberRow'
import { InviteModal } from './team-tab/InviteModal'
import type { InvitePayload } from './team-tab/InviteModal'

export interface TeamTabProps {
  apiClient?: SettingsApiClient
  organisationId?: string
  isAdmin?: boolean
  currentUserId?: number
  manageUrl?: string
  className?: string
}

export function TeamTab({
  apiClient,
  organisationId,
  isAdmin = false,
  currentUserId,
  manageUrl = 'https://dashboard.quotech.ai/settings?tab=team',
  className,
}: TeamTabProps) {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>(FALLBACK_ROLE_OPTIONS)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string; message: string; confirmLabel: string
    confirmVariant?: 'danger' | 'primary'; onConfirm: () => void
  } | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [activeMenu, setActiveMenu] = useState<number | null>(null)
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  const adminCount = useMemo(() => {
    return members.filter(m => {
      if ((m as any).hierarchy_level != null) return (m as any).hierarchy_level <= 25
      const code = (m.rbac_role_code || m.role || '').toLowerCase()
      return ['owner', 'admin', 'org_owner', 'org_admin', 'director', 'general_manager', 'operations_manager', 'workshop_manager'].includes(code)
    }).length
  }, [members])

  // ------ Data loading ------

  const [canInvite, setCanInvite] = useState(false)

  const loadRoles = useCallback(async () => {
    if (!apiClient || !organisationId) return
    try {
      const response = await apiClient.get(`/organisations/${organisationId}/assignable-roles/`)
      const res = response?.data ?? response
      const roles = res?.roles ?? []
      if (roles.length > 0) {
        const curated = roles
          .sort((a: any, b: any) => (a.hierarchy_level ?? 100) - (b.hierarchy_level ?? 100))
          .map((r: any) => ({ value: r.code, label: r.name, hierarchy_level: r.hierarchy_level ?? 100 }))
        setRoleOptions(curated)
      }
      if (res?.can_invite !== undefined) setCanInvite(res.can_invite)
    } catch {
      try {
        const fallbackRes = await apiClient.get(`/rbac/org/${organisationId}/roles/`)
        const fb = fallbackRes?.data ?? fallbackRes
        const all = [...(fb?.system_roles ?? []), ...(fb?.custom_roles ?? [])]
        if (all.length > 0) {
          const curated = all
            .filter((r: any) => (r.hierarchy_level ?? 100) >= 10)
            .sort((a: any, b: any) => (a.hierarchy_level ?? 100) - (b.hierarchy_level ?? 100))
            .map((r: any) => ({ value: r.code, label: r.name, hierarchy_level: r.hierarchy_level ?? 100 }))
          if (curated.length > 0) setRoleOptions(curated)
        }
      } catch { /* keep fallback roles */ }
    }
  }, [apiClient, organisationId])

  const loadMembers = useCallback(async () => {
    if (!apiClient || !organisationId) return
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`/organisations/${organisationId}/members/`)
      const data = response.data || response
      setMembers(data.members || [])
    } catch (err: any) {
      if (import.meta.env.DEV) console.error('[TeamTab] Failed to load members:', err)
      setError(err.message || 'Failed to load team members')
    } finally {
      setLoading(false)
    }
  }, [apiClient, organisationId])

  useEffect(() => { loadMembers(); loadRoles() }, [loadMembers, loadRoles])

  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      member.email.toLowerCase().includes(q) ||
      member.first_name?.toLowerCase().includes(q) ||
      member.last_name?.toLowerCase().includes(q) ||
      member.full_name?.toLowerCase().includes(q)
    )
  })

  // ------ Actions ------

  const handleInvite = async (payload: InvitePayload) => {
    if (!apiClient || !organisationId) return
    await apiClient.post(`/organisations/${organisationId}/members/`, payload)
    setShowInviteModal(false)
    setToast({ message: `Invitation sent to ${payload.email}`, type: 'success' })
    loadMembers()
  }

  const requestRoleChange = (member: TeamMember, newRole: string) => {
    if (currentUserId && member.id === currentUserId) {
      setToast({ message: "You cannot change your own role.", type: 'error' })
      setActiveMenu(null)
      return
    }
    const currentRoleValue = member.rbac_role_code || member.role
    if (currentRoleValue === newRole) { setActiveMenu(null); return }
    const roleName = roleOptions.find(r => r.value === newRole)?.label ?? newRole
    setConfirmDialog({
      title: 'Change role',
      message: `Change ${member.full_name || member.email}'s role to "${roleName}"?`,
      confirmLabel: 'Change role',
      confirmVariant: 'primary',
      onConfirm: () => { setConfirmDialog(null); executeRoleChange(member.id, newRole, member.full_name || member.email) },
    })
  }

  const executeRoleChange = async (memberId: number, newRole: string, memberName: string) => {
    if (!apiClient || !organisationId) return
    setActionLoading(memberId)
    try {
      await apiClient.put(`/organisations/${organisationId}/members/${memberId}/`, { role: newRole })
      setToast({ message: `${memberName}'s role updated to ${roleOptions.find(r => r.value === newRole)?.label ?? newRole}`, type: 'success' })
      loadMembers()
    } catch (err: any) {
      if (import.meta.env.DEV) console.error('[TeamTab] Failed to update role:', err)
      setToast({ message: err?.response?.data?.error || err?.message || 'Failed to update role', type: 'error' })
    } finally { setActionLoading(null); setActiveMenu(null) }
  }

  const requestRemoveMember = (member: TeamMember) => {
    if (currentUserId && member.id === currentUserId) {
      setToast({ message: "You can't remove yourself. Ask another admin to remove your account.", type: 'error' })
      setActiveMenu(null); return
    }
    const isAdminTier = (m: TeamMember) => {
      if ((m as any).hierarchy_level != null) return (m as any).hierarchy_level <= 25
      return ['owner', 'admin', 'org_owner', 'org_admin', 'director', 'general_manager', 'operations_manager', 'workshop_manager'].includes((m.rbac_role_code || m.role || '').toLowerCase())
    }
    if (isAdminTier(member) && adminCount <= 1) {
      setToast({ message: "Can't remove the last admin. Assign another admin first.", type: 'error' })
      setActiveMenu(null); return
    }
    setConfirmDialog({
      title: 'Remove member',
      message: `Remove ${member.full_name || member.email} from this organisation? They will lose all access.`,
      confirmLabel: 'Remove',
      confirmVariant: 'danger',
      onConfirm: () => { setConfirmDialog(null); executeRemoveMember(member.id, member.full_name || member.email) },
    })
  }

  const executeRemoveMember = async (memberId: number, memberName: string) => {
    if (!apiClient || !organisationId) return
    setActionLoading(memberId)
    try {
      await apiClient.delete(`/organisations/${organisationId}/members/${memberId}/`)
      setToast({ message: `${memberName} has been removed`, type: 'success' })
      loadMembers()
    } catch (err: any) {
      if (import.meta.env.DEV) console.error('[TeamTab] Failed to remove member:', err)
      setToast({ message: err?.response?.data?.error || err?.message || 'Failed to remove member', type: 'error' })
    } finally { setActionLoading(null); setActiveMenu(null) }
  }

  // RBAC V2 MR 19 FE (2026-05-29) — inline-edit capability toggles
  // (CFO / Team admin / Billing) for an existing member. Pre-MR-19, the
  // only way to set these toggles was at INVITE time via the InviteModal;
  // for an already-invited user the operator had to SSH into the
  // production DB and PATCH the row manually (see docs/architecture/
  // RBAC_OPERATOR_GUIDE.md §5.3, "Logs in but no CFO nav for finance
  // person"). The BE endpoint has accepted these fields since MR 12
  // (organisation/views/team_members_views/detail_views.py supports
  // partial-PATCH of has_finance_org_wide / is_org_admin_user /
  // is_billing_admin); only the FE was missing the affordance.
  //
  // The PUT body includes `role` because the BE expects the full
  // member record on PUT — falling back to the current role keeps the
  // mutation isolated to a single toggle. (A PATCH-only contract on
  // the BE would let us send just the changed field, but rewriting
  // the view to support PATCH is a separate MR.)
  type ToggleField = 'has_finance_org_wide' | 'is_org_admin_user' | 'is_billing_admin'
  const TOGGLE_LABELS: Record<ToggleField, string> = {
    has_finance_org_wide: 'CFO module access',
    is_org_admin_user: 'Team admin',
    is_billing_admin: 'Billing admin',
  }
  const handleToggleUpdate = async (
    member: TeamMember,
    field: ToggleField,
    newValue: boolean,
  ) => {
    if (!apiClient || !organisationId) return
    // Owner-tier members have all 3 toggles force-True on the BE — no
    // point firing a PATCH the server will overwrite. Surface the
    // constraint in a toast instead.
    if ((member.rbac_role_code || member.role || '').toLowerCase().includes('owner')) {
      setToast({
        message: `${TOGGLE_LABELS[field]} is always on for organisation owners.`,
        type: 'error',
      })
      setActiveMenu(null)
      return
    }
    setActionLoading(member.id)
    try {
      await apiClient.put(
        `/organisations/${organisationId}/members/${member.id}/`,
        {
          role: member.rbac_role_code || member.role,
          [field]: newValue,
        },
      )
      setToast({
        message: `${TOGGLE_LABELS[field]} ${newValue ? 'granted to' : 'removed from'} ${member.full_name || member.email}`,
        type: 'success',
      })
      loadMembers()
    } catch (err: any) {
      if (import.meta.env.DEV) console.error('[TeamTab] Failed to update toggle:', err)
      setToast({
        message: err?.response?.data?.error || err?.message || `Failed to update ${TOGGLE_LABELS[field]}`,
        type: 'error',
      })
    } finally {
      setActionLoading(null)
      setActiveMenu(null)
    }
  }

  const handleResendInvite = async (memberId: number, memberEmail: string) => {
    if (!apiClient || !organisationId) return
    // The same backend endpoint covers BOTH "resend invite" (member
    // never logged in) AND "reset password" (member is activated, admin
    // is force-resetting). Look up the activation state from the loaded
    // members list so we can confirm the destructive case (resetting an
    // activated user's password rotates the credential AND flips them
    // back into the temp-password flow on next login -- not something
    // we want to fire on a stray click).
    const member = members.find((m) => m.id === memberId)
    const isReset = member ? !member.is_temp_password : false
    if (isReset) {
      const ok = window.confirm(
        `Reset ${memberEmail}'s password?\n\n`
        + `Their current password will stop working immediately and a new `
        + `temporary password will be emailed to them. They'll be prompted `
        + `to choose a new password on their next sign-in.`
      )
      if (!ok) {
        setActiveMenu(null)
        return
      }
    }
    setActionLoading(memberId)
    try {
      const resp = await apiClient.post(`/organisations/${organisationId}/members/${memberId}/resend-invite/`)
      const mode = (resp?.data?.mode as 'resend' | 'reset' | undefined) ?? (isReset ? 'reset' : 'resend')
      setToast({
        message: mode === 'reset'
          ? `Password reset email sent to ${memberEmail}`
          : `Invitation resent to ${memberEmail}`,
        type: 'success',
      })
      loadMembers()
    } catch (err: any) {
      setToast({
        message: err?.response?.data?.error || err?.message
          || (isReset ? 'Failed to send password reset email' : 'Failed to resend invite'),
        type: 'error',
      })
    } finally { setActionLoading(null); setActiveMenu(null) }
  }

  // ------ Fallback (no API client) ------

  if (!apiClient || !organisationId) {
    return (
      <div className={cn('space-y-4 max-w-2xl', className)}>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Team Members</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Manage roles, invites, and access for your team.</p>
        </div>
        <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400"><Users className="w-5 h-5" /></div>
            <div>
              <p className="font-medium text-slate-800 dark:text-slate-200">Open team management</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">View members, roles, and invitation history in the main dashboard.</p>
            </div>
          </div>
          <button onClick={() => { window.location.href = manageUrl }} className="mt-4 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700">Manage Team</button>
        </div>
      </div>
    )
  }

  // ------ Main render ------

  return (
    <div className={cn('space-y-4', className)}>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title} message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel} confirmVariant={confirmDialog.confirmVariant}
          onConfirm={confirmDialog.onConfirm} onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Team Members</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">{members.length} member{members.length !== 1 ? 's' : ''} in your organisation</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadMembers} disabled={loading} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" title="Refresh">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </button>
          {(isAdmin || canInvite) && (
            <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700">
              <UserPlus className="w-4 h-4" />Invite
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" placeholder="Search members..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className={cn("w-full pl-10 pr-4 py-2 rounded-lg border", "border-slate-200 dark:border-slate-700", "bg-white dark:bg-slate-800", "text-slate-900 dark:text-white", "placeholder:text-slate-400", "focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500")} />
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">{error}</div>}

      {/* Members list */}
      <div className="space-y-2">
        {loading && members.length === 0 ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-8 text-slate-500">{searchQuery ? 'No members found' : 'No team members yet'}</div>
        ) : (
          filteredMembers.map((member) => (
            <MemberRow key={member.id} member={member} isAdmin={isAdmin}
              isSelf={!!(currentUserId && member.id === currentUserId)}
              roleOptions={roleOptions}
              activeMenu={activeMenu} actionLoading={actionLoading}
              onRoleChange={requestRoleChange} onRemove={requestRemoveMember}
              onResendInvite={handleResendInvite}
              onToggleUpdate={handleToggleUpdate}
              onToggleMenu={(id) => setActiveMenu(activeMenu === id ? null : id)} />
          ))
        )}
      </div>

      {showInviteModal && <InviteModal roleOptions={roleOptions} onInvite={handleInvite} onClose={() => setShowInviteModal(false)} />}
      {activeMenu && <div className="fixed inset-0 z-40" onClick={() => setActiveMenu(null)} />}
    </div>
  )
}

export default TeamTab
